import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { Box, IconButton, Skeleton, TextField, Typography } from "@mui/material";
import { InfoOutlined, ContentCopyRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import { BaseModal } from "./BaseModal";
import { PatientSummaryCard } from "./PatientSummaryCard";
import { useStateAuthValue } from "../../context/AuthState";
import { useToast } from "../../context/Toast/ToastProvider";
import { showToast, ToastType } from "../../utils/ToastUtil";
import { getPendingUnsubscriptionInfo } from "../../services/patientsService";

const InfoField = ({ label, value }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        color: "#8a929c",
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#002C54" }}>
      {value}
    </Typography>
  </Box>
);

// Visor de solo lectura de una baja pendiente (cooling-off): trae la info
// desde el mismo endpoint que resuelve el link público de arrepentimiento y
// permite copiar ese link. No hay edición: la cancela el paciente por su
// cuenta, o se gestiona por otra vía.
export const PendingUnsubscriptionModal = ({ open, setOpen, patient }) => {
  const [{ userToken }] = useStateAuthValue();
  const toastDispatch = useToast();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  const displayError = (msg) =>
    showToast(toastDispatch, msg, ToastType.ERROR, ToastType.ERROR);

  useEffect(() => {
    const fetchInfo = async () => {
      setLoadingInfo(true);
      const result = await getPendingUnsubscriptionInfo(
        userToken,
        patient?.unsubscriptionId,
        displayError
      );
      setInfo(result ?? null);
      setLoadingInfo(false);
    };
    if (patient?.unsubscriptionId) {
      fetchInfo();
    } else {
      setLoadingInfo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.unsubscriptionId]);

  const regretLink = patient?.unsubscriptionId
    ? `${process.env.REACT_APP_REGRET_URL}/${patient.unsubscriptionId}`
    : "";

  const copyRegretLink = () => {
    navigator.clipboard
      .writeText(regretLink)
      .then(() => {
        showToast(
          toastDispatch,
          "Link copiado al portapapeles.",
          ToastType.SUCCESS,
          ToastType.SUCCESS
        );
      })
      .catch(() => {
        displayError("No se pudo copiar el link.");
      });
  };

  return (
    <BaseModal width="600px" open={open} setOpen={setOpen}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {/* Header informativo (naranja): refleja el estado "Baja en proceso" */}
        <Box
          sx={{
            background: "linear-gradient(195deg, #EB6836, #b84d1f)",
            borderRadius: "15px",
            color: "white",
            padding: "1.25rem 1.5rem",
            boxShadow: "0 8px 24px rgba(235,104,54,0.25)",
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <InfoOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
              Baja en curso
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.85 }}>
              El socio está dentro del período de arrepentimiento
            </Typography>
          </Box>
        </Box>

        <PatientSummaryCard patient={patient} />

        {loadingInfo ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Skeleton variant="rounded" height={24} />
            <Skeleton variant="rounded" height={24} />
          </Box>
        ) : info ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "1rem",
              background: "#f6f8fa",
              border: "1px solid #e8ebef",
              borderRadius: "14px",
              padding: "1rem 1.25rem",
            }}
          >
            <InfoField
              label="Se hace efectiva el"
              value={
                info.deactivationDate
                  ? dayjs(info.deactivationDate).format("DD/MM/YYYY")
                  : "—"
              }
            />
            {info.planName && <InfoField label="Plan" value={info.planName} />}
            {info.price != null && (
              <InfoField
                label="Precio"
                value={`$${Number(info.price).toLocaleString("es-AR")}`}
              />
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: "13px", color: "#5a6470" }}>
            No se pudo obtener la información de la baja.
          </Typography>
        )}

        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              color: "#8a929c",
              marginBottom: "0.4rem",
            }}
          >
            Link de arrepentimiento
          </Typography>
          <Box sx={{ display: "flex", gap: "0.5rem" }}>
            <TextField
              fullWidth
              size="small"
              value={regretLink}
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
            <IconButton
              onClick={copyRegretLink}
              aria-label="Copiar link de arrepentimiento"
              sx={{
                borderRadius: "12px",
                background: "rgba(0,44,84,0.1)",
                color: "#002C54",
                "&:hover": { background: "rgba(0,44,84,0.18)" },
              }}
            >
              <ContentCopyRounded />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              paddingX: "1.5rem",
              color: "#5a6470",
              borderColor: "#d4d8dd",
              "&:hover": { borderColor: "#b9bfc6", background: "#f5f6f8" },
            }}
          >
            Cerrar
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};
